/**
 * Thin fetch wrapper. Every API call goes through here so we can:
 *   - send JSON consistently
 *   - attach the Clerk session JWT as a bearer token
 *   - centralise error handling (throw on non-2xx with a parsed body)
 *   - in dev, fall back to x-dev-email header from localStorage if
 *     DEV_AUTH_BYPASS=1 is set on the worker (offline-friendly dev)
 *
 * Token retrieval is registered once at app bootstrap via setAuthTokenGetter()
 * — App.tsx wires it up to Clerk's useAuth().getToken(). The wrapper itself
 * never imports Clerk so it stays usable in non-Clerk surfaces (Storybook,
 * tests).
 */

import type {
  BoardDetail,
  BoardSummary,
  Cluster,
  IdeaWithReactions,
  Plan,
  PlanState,
  Session,
  Phase,
  User,
} from '../shared/types';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export { ApiError };
export const DEV_EMAIL_KEY = 'synfolio.dev.email';

/**
 * A row in the inbox / per-board task strip. Discriminated by `kind`:
 *  - 'mention': a real comment that @-mentions me
 *  - 'nudge':   a synthetic prompt (e.g. write your intent)
 */
export type TaskRow =
  | {
      kind: 'mention';
      commentId: string;
      boardId: string;
      boardName: string;
      subjectKind: 'idea' | 'session' | 'cluster';
      subjectId: string;
      subjectLabel: string;
      authorId: string;
      authorName: string;
      body: string;
      bodyNodes: import('../shared/comment-body').CommentBody;
      dueIso: string | null;
      createdAt: number;
      completedAt: number | null;
      href: string;
    }
  | {
      kind: 'nudge';
      nudgeId: string;
      nudgeKind: 'write_intent';
      boardId: string;
      boardName: string;
      body: string;
      bodyNodes: import('../shared/comment-body').CommentBody;
      dueIso: string | null;
      createdAt: number;
      completedAt: number | null;
      href: string;
    };

type TokenGetter = () => Promise<string | null>;
let getAuthToken: TokenGetter = async () => null;

/** Called once at boot by App.tsx, after ClerkProvider is mounted. */
export function setAuthTokenGetter(fn: TokenGetter): void {
  getAuthToken = fn;
}

async function applyAuthHeaders(headers: Record<string, string>): Promise<void> {
  const token = await getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    return;
  }
  // Offline dev fallback — only used when Clerk isn't configured.
  // VITE_DEV_AUTH=1 turns the same path on for built bundles (e.g. our
  // Playwright E2E server). Production builds without that flag will
  // skip the header entirely.
  if (import.meta.env.DEV || import.meta.env.VITE_DEV_AUTH === '1') {
    const email = localStorage.getItem(DEV_EMAIL_KEY);
    if (email) {
      headers['x-dev-email'] = email;
      const name = email.split('@')[0]!.replace(/[._]/g, ' ');
      headers['x-dev-name'] = name.charAt(0).toUpperCase() + name.slice(1);
    }
  }
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  await applyAuthHeaders(headers);

  const res = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  if (!res.ok) {
    let msg = res.statusText;
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) msg = j.error;
    } catch {
      /* ignored */
    }
    throw new ApiError(res.status, msg);
  }
  return res.json() as Promise<T>;
}

export const api = {
  me: () =>
    req<{
      user: User;
      tenant: { id: string; slug: string; name: string } | null;
      workspaces: Array<{
        id: string;
        slug: string;
        name: string;
        isPersonal: boolean;
        role: 'admin' | 'member';
      }>;
    }>('GET', '/api/me'),

  myWorkspaces: () =>
    req<{
      workspaces: Array<{
        id: string;
        slug: string;
        name: string;
        isPersonal: boolean;
        role: 'admin' | 'member';
        joinedAt: number;
      }>;
      activeId: string;
    }>('GET', '/api/me/workspaces'),
  switchWorkspace: (workspaceId: string) =>
    req<{ ok: true; activeId: string }>('PUT', '/api/me/active-workspace', { workspaceId }),
  createWorkspace: (name: string, switchTo = true) =>
    req<{ ok: true; id: string; slug: string; switched: boolean }>(
      'POST',
      '/api/me/workspaces',
      { name, switchTo },
    ),
  myBoards: () => req<{ boards: BoardSummary[] }>('GET', '/api/me/boards'),

  createBoard: (body: {
    name: string;
    tagline?: string;
    groupType?: 'team' | 'colleagues' | 'friends' | 'family' | 'mates' | 'other';
    startsOn?: string;
    endsOn?: string;
    dateMode?: 'fixed' | 'fuzzy' | 'unset';
    dateHint?: string;
  }) =>
    req<{ id: string }>('POST', '/api/boards', body),
  board: (id: string) => req<BoardDetail>('GET', `/api/boards/${id}`),
  updateBoard: (
    id: string,
    body: Partial<{
      name: string;
      tagline: string | null;
      startsOn: string | null;
      endsOn: string | null;
      dateMode: import('../shared/types').DateMode;
      dateHint: string | null;
      makeupKids: string | null;
      makeupDietary: string | null;
      makeupAccessibility: string | null;
      priority: import('../shared/types').BoardPriority;
    }>,
  ) => req<{ ok: true }>('PATCH', `/api/boards/${id}`, body),
  setPhase: (id: string, phase: Phase) =>
    req<{ ok: true }>('POST', `/api/boards/${id}/phase`, { phase }),
  invite: (id: string, email: string) =>
    req<{ ok: true }>('POST', `/api/boards/${id}/invites`, { email }),
  resendInvite: (id: string, email: string) =>
    req<{ ok: true }>('POST', `/api/boards/${id}/invites/resend`, { email }),

  /** Owner-only: remove someone else from the board. */
  removeMember: (boardId: string, memberId: string) =>
    req<{ ok: true }>('DELETE', `/api/boards/${boardId}/members/${memberId}`),
  /** Self-service: remove me from the board. */
  leaveBoard: (boardId: string) =>
    req<{ ok: true }>('POST', `/api/boards/${boardId}/members/leave`),
  /** Owner-only: pass the keys to another member. */
  transferOwnership: (boardId: string, memberId: string) =>
    req<{ ok: true }>(
      'POST',
      `/api/boards/${boardId}/members/${memberId}/transfer-ownership`,
    ),

  ideas: (boardId: string) =>
    req<{ ideas: IdeaWithReactions[] }>('GET', `/api/boards/${boardId}/ideas`),
  createIdea: (boardId: string, body: { text: string; note?: string }) =>
    req<{ id: string; emoji: string | null; colorHue: number | null }>(
      'POST',
      `/api/boards/${boardId}/ideas`,
      body,
    ),
  patchIdea: (boardId: string, ideaId: string, body: { text?: string; note?: string | null }) =>
    req<{ ok: true }>('PATCH', `/api/boards/${boardId}/ideas/${ideaId}`, body),
  deleteIdea: (boardId: string, ideaId: string) =>
    req<{ ok: true }>('DELETE', `/api/boards/${boardId}/ideas/${ideaId}`),
  react: (boardId: string, ideaId: string, kind: 'love' | 'curious' | 'pass' | null) =>
    req<{ ok: true; reactions: { love: number; curious: number; pass: number } }>(
      'POST',
      `/api/boards/${boardId}/ideas/${ideaId}/reaction`,
      { kind },
    ),
  addLink: (boardId: string, ideaId: string, url: string) =>
    req<{ id: string; attachment: import('../shared/types').IdeaAttachment }>(
      'POST',
      `/api/boards/${boardId}/ideas/${ideaId}/attachments/link`,
      { url },
    ),
  uploadImage: async (boardId: string, ideaId: string, file: File) => {
    const headers: Record<string, string> = { 'content-type': file.type };
    await applyAuthHeaders(headers);
    const res = await fetch(`/api/boards/${boardId}/ideas/${ideaId}/attachments/image`, {
      method: 'POST',
      headers,
      body: file,
      credentials: 'include',
    });
    if (!res.ok) throw new ApiError(res.status, await res.text());
    return res.json() as Promise<{ id: string; r2Key: string }>;
  },

  clusters: (boardId: string) =>
    req<{ clusters: Cluster[] }>('GET', `/api/boards/${boardId}/clusters`),
  suggestClusters: (boardId: string) =>
    req<{ suggestions: Array<{ title: string; ideaIds: string[] }> }>(
      'POST',
      `/api/boards/${boardId}/clusters/suggest`,
    ),
  applyClusters: (boardId: string, suggestions: Array<{ title: string; ideaIds: string[] }>) =>
    req<{ ok: true }>('POST', `/api/boards/${boardId}/clusters/apply`, { suggestions }),
  patchCluster: (
    boardId: string,
    clusterId: string,
    body: Partial<{
      title: string;
      colorHue: number;
      scheduledDate: string | null;
      scheduledSlot: 'morning' | 'afternoon' | 'evening' | null;
    }>,
  ) =>
    req<{ ok: true }>('PATCH', `/api/boards/${boardId}/clusters/${clusterId}`, body),
  assignIdea: (boardId: string, ideaId: string, clusterId: string | null) =>
    req<{ ok: true }>('POST', `/api/boards/${boardId}/clusters/ideas/${ideaId}/assign`, {
      clusterId,
    }),

  getPlan: (boardId: string) =>
    req<{ plan: Plan | null }>('GET', `/api/boards/${boardId}/plan`),
  shipPlan: (boardId: string) =>
    req<{ ok: true; markdown: string }>('POST', `/api/boards/${boardId}/plan/ship`),

  // v2: sessions + chat
  planState: (boardId: string, branchId?: string | null) =>
    req<PlanState>(
      'GET',
      `/api/boards/${boardId}/plan-state${branchId ? `?branch=${encodeURIComponent(branchId)}` : ''}`,
    ),

  listBranches: (boardId: string) =>
    req<{
      branches: Array<{
        id: string;
        name: string;
        parentId: string | null;
        createdBy: string;
        description: string | null;
        colorHue: number;
        createdAt: number;
      }>;
    }>('GET', `/api/boards/${boardId}/branches`),
  forkBranch: (
    boardId: string,
    body: { name: string; description?: string; parentBranchId?: string | null },
  ) =>
    req<{ id: string; name: string; sessionCount: number }>(
      'POST',
      `/api/boards/${boardId}/branches`,
      body,
    ),
  combineBranches: (
    boardId: string,
    body: { sourceIds: Array<string | null>; name: string; description?: string },
  ) =>
    req<{ id: string; name: string; sessionCount: number }>(
      'POST',
      `/api/boards/${boardId}/branches/combine`,
      body,
    ),
  mergeBranch: (boardId: string, branchId: string) =>
    req<{ ok: true }>('POST', `/api/boards/${boardId}/branches/${branchId}/merge`),
  deleteBranch: (boardId: string, branchId: string) =>
    req<{ ok: true }>('DELETE', `/api/boards/${boardId}/branches/${branchId}`),
  generatePlan: (boardId: string, branchId?: string | null) =>
    req<{ ok: true; state: PlanState; summary: string }>(
      'POST',
      `/api/boards/${boardId}/plan/generate${branchId ? `?branch=${encodeURIComponent(branchId)}` : ''}`,
    ),
  chatPlan: (boardId: string, message: string, branchId?: string | null) =>
    req<{ ok: true; message: string; state: PlanState }>(
      'POST',
      `/api/boards/${boardId}/plan/chat`,
      { message, branchId: branchId ?? null },
    ),
  createSession: (
    boardId: string,
    body: {
      name: string;
      description?: string | null;
      date: string;
      startMinute: number;
      endMinute: number;
      ownerId?: string | null;
      themeIds?: string[];
      colorHue?: number;
      isRevealed?: boolean;
      branchId?: string | null;
    },
  ) => req<{ session: Session }>('POST', `/api/boards/${boardId}/sessions`, body),
  patchSession: (
    boardId: string,
    sessionId: string,
    body: Partial<{
      name: string;
      description: string | null;
      date: string;
      startMinute: number;
      endMinute: number;
      ownerId: string | null;
      themeIds: string[];
      attendees: Array<{ userId: string; required: boolean }>;
      colorHue: number;
      isRevealed: boolean;
      position: number;
    }>,
  ) =>
    req<{ ok: true; session: Session }>(
      'PATCH',
      `/api/boards/${boardId}/sessions/${sessionId}`,
      body,
    ),
  deleteSession: (boardId: string, sessionId: string) =>
    req<{ ok: true }>('DELETE', `/api/boards/${boardId}/sessions/${sessionId}`),
  setAvailability: (
    boardId: string,
    body: { date: string; userId?: string; available: boolean; note?: string | null },
  ) => req<{ ok: true }>('POST', `/api/boards/${boardId}/availability`, body),
  upsertDay: (
    boardId: string,
    body: {
      date: string;
      label?: string | null;
      workingStartMinute?: number | null;
      workingEndMinute?: number | null;
    },
  ) => req<{ ok: true; id: string }>('POST', `/api/boards/${boardId}/days`, body),
  listPriorities: (boardId: string) =>
    req<{
      priorities: Array<{
        userId: string;
        clusterId: string;
        weight: number;
        rank: number | null;
        lockedAt: number | null;
      }>;
    }>('GET', `/api/boards/${boardId}/priorities`),
  setPriorities: (
    boardId: string,
    body: {
      priorities: Array<{ clusterId: string; weight: number; rank?: number | null }>;
      lock?: boolean;
    },
  ) => req<{ ok: true }>('POST', `/api/boards/${boardId}/priorities`, body),

  listComments: (boardId: string, kind: 'idea' | 'session' | 'cluster', subjectId: string) =>
    req<{
      comments: Array<{
        id: string;
        authorId: string;
        authorName: string;
        authorHue: number;
        parentId: string | null;
        body: string;
        emoji: string | null;
        createdAt: number;
        updatedAt: number;
      }>;
    }>(
      'GET',
      `/api/boards/${boardId}/comments/${kind}/${encodeURIComponent(subjectId)}`,
    ),
  createComment: (
    boardId: string,
    kind: 'idea' | 'session' | 'cluster',
    subjectId: string,
    body: { body: string; emoji?: string; parentId?: string | null },
  ) =>
    req<{
      comment: {
        id: string;
        authorId: string;
        authorName: string;
        authorHue: number;
        parentId: string | null;
        body: string;
        emoji: string | null;
        createdAt: number;
        updatedAt: number;
      };
    }>(
      'POST',
      `/api/boards/${boardId}/comments/${kind}/${encodeURIComponent(subjectId)}`,
      body,
    ),

  myTasks: (boardId?: string) =>
    req<{
      tasks: Array<TaskRow>;
    }>('GET', boardId ? `/api/boards/${boardId}/me/tasks` : '/api/me/tasks'),
  completeTask: (commentId: string) =>
    req<{ ok: true }>('POST', `/api/me/tasks/${commentId}/done`),
  uncompleteTask: (commentId: string) =>
    req<{ ok: true }>('DELETE', `/api/me/tasks/${commentId}/done`),
  patchComment: (
    boardId: string,
    commentId: string,
    body: { body?: string; emoji?: string | null },
  ) =>
    req<{ ok: true }>(
      'PATCH',
      `/api/boards/${boardId}/comments/${encodeURIComponent(commentId)}`,
      body,
    ),
  deleteComment: (boardId: string, commentId: string) =>
    req<{ ok: true }>(
      'DELETE',
      `/api/boards/${boardId}/comments/${encodeURIComponent(commentId)}`,
    ),

  notifications: () =>
    req<{
      notifications: Array<{
        id: string;
        boardId: string | null;
        actorId: string | null;
        kind: string;
        summary: string;
        href: string | null;
        readAt: number | null;
        createdAt: number;
      }>;
    }>('GET', '/api/me/notifications'),
  unreadNotifications: () =>
    req<{
      unread: number;
      items: Array<{ id: string; summary: string; href: string | null; createdAt: number }>;
    }>('GET', '/api/me/notifications/unread'),
  markNotificationRead: (id: string) =>
    req<{ ok: true }>('POST', `/api/me/notifications/${encodeURIComponent(id)}/read`),
  ackNotification: (id: string) =>
    req<{ ok: true }>('POST', `/api/me/notifications/${encodeURIComponent(id)}/ack`),
  markAllNotificationsRead: () =>
    req<{ ok: true }>('POST', '/api/me/notifications/mark-all-read'),

  myIntent: (boardId: string) =>
    req<{ intent: { body: string; updatedAt: number } | null }>(
      'GET',
      `/api/boards/${boardId}/me/intent`,
    ),
  saveMyIntent: (boardId: string, body: string) =>
    req<{ ok: true; updatedAt: number }>('PUT', `/api/boards/${boardId}/me/intent`, { body }),
  intents: (boardId: string) =>
    req<{
      me: { body: string; updatedAt: number } | null;
      status: Array<{ userId: string; name: string; filled: boolean; isMe: boolean }>;
      contributorCount: number;
      memberCount: number;
      pendingInviteCount: number;
      pendingInviteEmails: string[];
      summary: {
        summaryMd: string;
        contributorCount: number;
        updatedAt: number;
        stale: boolean;
      } | null;
    }>('GET', `/api/boards/${boardId}/intents`),
  synthIntents: (boardId: string) =>
    req<{
      summary: { summaryMd: string; contributorCount: number; updatedAt: number; stale: boolean };
    }>('POST', `/api/boards/${boardId}/intents/synth`),
  myIntentHistory: (boardId: string) =>
    req<{ versions: Array<{ id: string; body: string; createdAt: number }> }>(
      'GET',
      `/api/boards/${boardId}/me/intent/history`,
    ),

  patchProfile: (body: {
    name?: string;
    avatarHue?: number;
    themePreference?: string | null;
  }) => req<{ user: import('../shared/types').User }>('PATCH', '/api/me', body),
  deleteAvatar: () => req<{ ok: true }>('DELETE', '/api/me/avatar'),

  myTenant: () =>
    req<{
      tenant: {
        id: string;
        slug: string;
        name: string;
        emailDomain: string;
        isPersonal: boolean;
        createdAt: number;
      };
      members: Array<{
        id: string;
        email: string;
        name: string;
        avatarHue: number;
        avatarUrl: string | null;
        tenantRole: 'admin' | 'member';
        lastSeenAt: number;
        createdAt: number;
      }>;
      pendingInvites: Array<{ email: string; invitedBy: string; createdAt: number }>;
      boardCount: number;
      myRole: 'admin' | 'member';
    }>('GET', '/api/me/tenant'),
  patchTenant: (body: { name?: string }) =>
    req<{ ok: true }>('PATCH', '/api/me/tenant', body),
  setTenantMemberRole: (uid: string, role: 'admin' | 'member') =>
    req<{ ok: true }>('POST', `/api/me/tenant/members/${uid}/role`, { role }),
  inviteToTenant: (email: string) =>
    req<{ ok: true; already?: boolean; invited?: boolean }>(
      'POST',
      '/api/me/tenant/invites',
      { email },
    ),
  cancelTenantInvite: (email: string) =>
    req<{ ok: true }>(
      'DELETE',
      `/api/me/tenant/invites/${encodeURIComponent(email)}`,
    ),

  isBoardMuted: (boardId: string) =>
    req<{ muted: boolean; mutedAt: number | null }>('GET', `/api/boards/${boardId}/mute`),
  muteBoard: (boardId: string) =>
    req<{ muted: true }>('PUT', `/api/boards/${boardId}/mute`),
  unmuteBoard: (boardId: string) =>
    req<{ muted: false }>('DELETE', `/api/boards/${boardId}/mute`),

  toggleSessionComplete: (boardId: string, sessionId: string) =>
    req<{ ok: true; completed: boolean }>(
      'POST',
      `/api/boards/${boardId}/sessions/${sessionId}/complete`,
    ),
  listSessionCompletions: (boardId: string, sessionId: string) =>
    req<{
      completions: Array<{ userId: string; completedAt: number; name: string; avatarHue: number }>;
    }>('GET', `/api/boards/${boardId}/sessions/${sessionId}/completions`),

  lobbyFeedback: (boardId: string) =>
    req<{
      feedback: Array<{
        id: string;
        kind: 'comment' | 'love' | 'curious' | 'pass';
        body: string | null;
        name: string | null;
        email: string | null;
        sessionId: string | null;
        createdAt: number;
      }>;
    }>('GET', `/api/boards/${boardId}/lobby-feedback`),
  shareDeck: (boardId: string) =>
    req<{ token: string }>('POST', `/api/boards/${boardId}/deck/share`),
  revokeDeck: (boardId: string) =>
    req<{ ok: true }>('DELETE', `/api/boards/${boardId}/deck/share`),
  patchBoardCosmetic: (
    boardId: string,
    body: {
      visualTheme?: string | null;
      accentHue?: number | null;
      workingStartMinute?: number;
      workingEndMinute?: number;
    },
  ) => req<{ ok: true }>('PATCH', `/api/boards/${boardId}/board-cosmetic`, body),

  // Session submissions (during/after a session)
  listSubmissions: (boardId: string, sessionId: string) =>
    req<{ submissions: import('../shared/types').SessionSubmission[] }>(
      'GET',
      `/api/boards/${boardId}/sessions/${sessionId}/submissions`,
    ),
  createSubmission: (
    boardId: string,
    sessionId: string,
    body: { kind: 'text' | 'link'; text?: string; url?: string; title?: string; description?: string },
  ) =>
    req<{ id: string }>(
      'POST',
      `/api/boards/${boardId}/sessions/${sessionId}/submissions`,
      body,
    ),
  uploadSubmissionImage: async (boardId: string, sessionId: string, file: File) => {
    const headers: Record<string, string> = { 'content-type': file.type };
    await applyAuthHeaders(headers);
    const res = await fetch(
      `/api/boards/${boardId}/sessions/${sessionId}/submissions/image`,
      { method: 'POST', headers, body: file, credentials: 'include' },
    );
    if (!res.ok) throw new ApiError(res.status, await res.text());
    return res.json() as Promise<{ id: string; r2Key: string }>;
  },
  toggleSubmissionWinner: (boardId: string, sessionId: string, submissionId: string) =>
    req<{ ok: true; isWinner: boolean }>(
      'POST',
      `/api/boards/${boardId}/sessions/${sessionId}/submissions/${submissionId}/winner`,
    ),
  voteSubmission: (boardId: string, sessionId: string, submissionId: string) =>
    req<{ ok: true }>(
      'POST',
      `/api/boards/${boardId}/sessions/${sessionId}/submissions/${submissionId}/vote`,
    ),
  deleteSubmission: (boardId: string, sessionId: string, submissionId: string) =>
    req<{ ok: true }>(
      'DELETE',
      `/api/boards/${boardId}/sessions/${sessionId}/submissions/${submissionId}`,
    ),

  pushPublicKey: () => req<{ key: string | null }>('GET', '/api/push/public-key'),
  pushSubscribe: (sub: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    req<{ ok: true }>('POST', '/api/push/subscribe', sub),

  shareIntent: () =>
    req<{
      intent: {
        id: string;
        text: string;
        url: string | null;
        imageR2Key: string | null;
        createdAt: number;
      } | null;
    }>('GET', '/share/intent'),

  // ---- Calendars + working hours + outbound feed --------------------

  listCalendars: () =>
    req<{
      links: Array<{
        id: string;
        provider: 'ics' | 'google' | 'microsoft';
        displayName: string | null;
        status: 'active' | 'error' | 'revoked';
        lastError: string | null;
        lastSyncedAt: number | null;
        createdAt: number;
      }>;
    }>('GET', '/api/calendars'),
  addIcsCalendar: (url: string, displayName?: string) =>
    req<{ id: string; ok: boolean; reason: string | null }>(
      'POST',
      '/api/calendars/ics',
      { url, displayName },
    ),
  syncCalendar: (id: string) =>
    req<{ ok: boolean; reason: string | null }>(
      'POST',
      `/api/calendars/${encodeURIComponent(id)}/sync`,
    ),
  deleteCalendar: (id: string) =>
    req<{ ok: true }>('DELETE', `/api/calendars/${encodeURIComponent(id)}`),
  oauthStatus: () =>
    req<{ google: boolean; microsoft: boolean }>('GET', '/api/calendars/oauth/status'),
  listCalendarEvents: (from: number, to: number) =>
    req<{
      events: Array<{
        startsAt: number;
        endsAt: number;
        busy: boolean;
        allDay: boolean;
        title: string | null;
      }>;
    }>('GET', `/api/calendars/events?from=${from}&to=${to}`),

  getWorkingHours: () =>
    req<{
      timezone: string;
      days: Array<{ dayOfWeek: number; startMinute: number; endMinute: number }>;
    }>('GET', '/api/me/working-hours'),
  putWorkingHours: (body: {
    timezone: string;
    days: Array<{ dayOfWeek: number; startMinute: number; endMinute: number }>;
  }) => req<{ ok: true }>('PUT', '/api/me/working-hours', body),

  getCalendarFeed: () =>
    req<{ token: string | null; url: string | null }>('GET', '/api/me/calendar-feed'),
  rotateCalendarFeed: () =>
    req<{ token: string; url: string }>('POST', '/api/me/calendar-feed/rotate'),

  listCalendarShares: () =>
    req<{ shares: CalendarShareSummary[] }>('GET', '/api/me/calendar-shares'),
  createCalendarShare: (body: CalendarShareInput) =>
    req<{ id: string; url: string; token: string }>(
      'POST',
      '/api/me/calendar-shares',
      body,
    ),
  updateCalendarShare: (id: string, body: Partial<CalendarShareInput>) =>
    req<{ ok: true }>(
      'PATCH',
      `/api/me/calendar-shares/${encodeURIComponent(id)}`,
      body,
    ),
  revokeCalendarShare: (id: string) =>
    req<{ ok: true }>(
      'POST',
      `/api/me/calendar-shares/${encodeURIComponent(id)}/revoke`,
    ),
  previewCalendarShare: (id: string) =>
    req<{ events: CalendarSharePreviewEvent[]; todos: CalendarSharePreviewTodo[] }>(
      'GET',
      `/api/me/calendar-shares/${encodeURIComponent(id)}/preview`,
    ),

  // ---- Personal tasks + scheduling preferences ---------------------

  listPersonalTasks: (status?: string) =>
    req<{
      tasks: Array<PersonalTask>;
    }>(
      'GET',
      status ? `/api/tasks?status=${encodeURIComponent(status)}` : '/api/tasks',
    ),
  createPersonalTask: (body: {
    title: string;
    notes?: string;
    workLevel?: 'deep' | 'shallow' | 'admin';
    estimatedMinutes?: number;
    sourceBoardId?: string;
    sourceSessionId?: string;
    defer?: boolean;
  }) =>
    req<{
      task: PersonalTask;
      scheduled: boolean;
      mode?: 'always' | 'ask' | 'never';
      reason?: 'no-working-hours' | 'no-room' | 'level-disabled' | 'deferred';
    }>('POST', '/api/tasks', body),
  patchPersonalTask: (
    id: string,
    body: Partial<{
      title: string;
      notes: string | null;
      workLevel: 'deep' | 'shallow' | 'admin';
      estimatedMinutes: number;
      status: 'inbox' | 'proposed' | 'scheduled' | 'done' | 'dismissed';
      scheduledStartsAt: number | null;
      scheduledEndsAt: number | null;
    }>,
  ) => req<{ task: PersonalTask | null }>('PATCH', `/api/tasks/${encodeURIComponent(id)}`, body),
  deletePersonalTask: (id: string) =>
    req<{ ok: true }>('DELETE', `/api/tasks/${encodeURIComponent(id)}`),
  acceptTaskProposal: (id: string) =>
    req<{ task: PersonalTask | null; changed: boolean }>(
      'POST',
      `/api/tasks/${encodeURIComponent(id)}/accept`,
    ),
  reschedulePersonalTask: (id: string) =>
    req<{ task: PersonalTask | null; found: boolean; reason?: string }>(
      'POST',
      `/api/tasks/${encodeURIComponent(id)}/reschedule`,
    ),

  getSchedulingPrefs: () =>
    req<{ prefs: Array<SchedulingPref> }>('GET', '/api/me/scheduling-prefs'),
  putSchedulingPrefs: (prefs: Array<SchedulingPref>) =>
    req<{ ok: true }>('PUT', '/api/me/scheduling-prefs', { prefs }),
};

export interface PersonalTask {
  id: string;
  title: string;
  notes: string | null;
  workLevel: 'deep' | 'shallow' | 'admin';
  estimatedMinutes: number;
  status: 'inbox' | 'proposed' | 'scheduled' | 'done' | 'dismissed';
  scheduledStartsAt: number | null;
  scheduledEndsAt: number | null;
  sourceBoardId: string | null;
  sourceSessionId: string | null;
  proposedAt: number | null;
  completedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface SchedulingPref {
  workLevel: 'deep' | 'shallow' | 'admin';
  mode: 'always' | 'ask' | 'never';
  minBlockMinutes: number;
  defaultMinutes: number;
}

export type CalendarShareLens = 'busy' | 'headlines' | 'participant' | 'detail';

export interface CalendarShareInput {
  label: string;
  audience?: string;
  lens: CalendarShareLens;
  includeTasks: boolean;
  includeDescriptions: boolean;
  includeUrls: boolean;
  boardIds: string[] | null;
  /** Days from now until expiry. null = no expiry. */
  expiresInDays: number | null;
}

export interface CalendarShareSummary {
  id: string;
  label: string;
  audience: string | null;
  lens: CalendarShareLens;
  includeTasks: boolean;
  includeDescriptions: boolean;
  includeUrls: boolean;
  boardIds: string[] | null;
  expiresAt: number | null;
  createdAt: number;
  url: string;
  lastAccessAt: number | null;
  lastAccessUa: string | null;
  accessCount: number;
  uniqueSubscribers: number;
}

export interface CalendarSharePreviewEvent {
  title: string;
  startsAt: number;
  endsAt: number;
  description: string | null;
  status: 'CONFIRMED' | 'TENTATIVE' | 'CANCELLED';
}

export interface CalendarSharePreviewTodo {
  title: string;
  status: string;
}
